import React, { useEffect, useState } from 'react';
import { OverlayContainer } from '@react-aria/overlays';
import { Item } from '@react-stately/collections';
import { useOverlayTriggerState } from '@react-stately/overlays';
import {
  Banner,
  Button,
  Modal,
  TextField,
  Typography,
  Checkbox,
  SpinnerInline,
  Picker,
  IconSettings
} from '@riac/design-system';
import { IconDownload } from '@riac/design-system';
import { useFetch } from 'src/lib/FetchContext';
import { LoginToUse } from '../LoginToUse/LoginToUse';
import { SearchPostData } from '../../pages/CatalogPage/CatalogPage.i';
import { ThingSwitcher, Thing } from '@riac/design-system';
import { validateEmail } from '@riac/core/util';

export type EBookDownloadProps = React.PropsWithChildren<{
  searchPostData: SearchPostData,
  pdfTitle?: string,
  auctionId: number,
  title: string,
  label?: string,
  forceFilters?: boolean,
  isDisabled?: boolean,
  noOutline?: boolean,
}>;

export const EBookDownload = ({
  searchPostData,
  pdfTitle = undefined,
  auctionId,
  title,
  label='',
  forceFilters = false,
  isDisabled = false,
  noOutline = false,
}: EBookDownloadProps): JSX.Element => {
  const { fetch } = useFetch();

  const [tooManyRequests, setTooManyRequests] = useState<string>('');
  const [includeFilters, setIncludeFilters] = useState(true);
  const [loading, setLoading] = useState<boolean>(false);

  const epubExportModalState = useOverlayTriggerState({});
  const [epubExportModalDisplay, setEpubExportModalDisplay] = useState<string>('');
  const [epubDownload, setEpubDownload] = useState<boolean | Blob>(false);
  const [epubGenerationError, setEpubGenerationError] = useState<string>('');

  const pdfExportModalState = useOverlayTriggerState({});
  const [pdfExportModalDisplay, setPdfExportModalDisplay] = useState<string>('');
  const [emailAddress, setEmailAddress] = useState('');
  const [showPdfAdvancedOptions, setShowPdfAdvancedOptions] = useState(false);
  const [paperSize, setPaperSize] = useState('letter');
  const [pageOrientation, setPageOrientation] = useState('portrait');
  const [fontSize, setFontSize] = useState('12');
  const [marginSize, setMarginSize] = useState('32');
  const [inputErrors, setInputErrors] = useState([]);
  const [pdfGenerationError, setPdfGenerationError] = useState<string>('');

  useEffect(() => {
    setPaperSize('letter');
    setShowPdfAdvancedOptions(false);
    setEpubDownload(false);
    setTooManyRequests('');
  }, [pdfExportModalState.isOpen, epubExportModalState.isOpen]);

  const startPDFGeneration = async (): Promise<void> => {
    setPdfGenerationError('');
    setTooManyRequests('');
    if(!paperSize) {
      setInputErrors(['You must select a paper size']);
      return;
    }
    else if(!validateEmail(emailAddress))
    {
      setInputErrors(['You must enter a valid email address']);
      return;
    }
    else
    {
      setInputErrors([]);
      setLoading(true);
    }
    const pdfResponse = await fetch(`/api/ebook/pdf/start/${auctionId}`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        searchPostData: includeFilters ? searchPostData : { auctionid: auctionId },
        pdfOptions: {
          pdfTitle: pdfTitle,
          paperSize: paperSize,
          pageOrientation: pageOrientation,
          fontSize: fontSize,
          marginSize: marginSize,
        },
        email: encodeURIComponent(emailAddress),
      }),
    });

    setLoading(false);

    if(pdfResponse.status === 429) {
      setTooManyRequests('Too many requests today. Try again tomorrow');
      return;
    }
    const pdfResult = await pdfResponse.json();

    setPdfExportModalDisplay('result');
    if(pdfResult.success == false) {
      if(pdfResult.error == 'No Items Returned') {
        // eslint-disable-next-line max-len
        setPdfGenerationError('Cannot Create PDF With Blank Search Results.  Try Another Search Or Deselect "Include only the lots that match my filters"');
      }
    }
  };

  const startEpubDownload = async (): Promise<void> => {
    setTooManyRequests('');
    setEpubGenerationError('');
    setEpubDownload(true);
    setLoading(true);

    const epubResponse = await fetch(`/api/ebook/epub/download/${auctionId}/${encodeURIComponent(title)}`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        searchPostData: includeFilters ? searchPostData : { auctionid: auctionId },
      }),
    });

    setLoading(false);
    if(epubResponse.status === 429) {
      setTooManyRequests('Too many requests today. Try again tomorrow');
      setEpubDownload(false);
      return;
    }

    const epubResult = await epubResponse.json();

    if(epubResult.status == false) {
      setEpubExportModalDisplay('result');
      if(epubResult.error == 'No Items Returned') {
        // eslint-disable-next-line max-len
        setEpubGenerationError('Cannot Create EPUB With Blank Search Results.  Try Another Search Or Deselect "Include only the lots that match my filters"');
      }
    } else {
      const a = document.createElement('a');
      a.style.display = 'none';
      a.setAttribute('href', epubResult.url);
      a.setAttribute('download', `${title}.epub`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setEpubDownload(false);
      epubExportModalState.close();
    }
  };

  return (
    <div className='flex items-center'>
      <LoginToUse
        overlayText = 'Log In to download catalog'
        childrenWrapperClassName='flex-1 flex p-1'
        className='flex'
      >
        <div className='flex flex-wrap gap-5'>
          <Button variant='secondary' className={noOutline && 'border-transparent px-0 hover:border-transparent active:border-transparent'} onPress={() => {
            setPdfExportModalDisplay('input');
            pdfExportModalState.open();
          }}>
            <Typography weight='bold' className='text-text-lighter hover:text-text-light transition-colors flex gap-1 items-center justify-center'><IconDownload/> PDF</Typography>
          </Button>
          <Button variant='secondary' className={noOutline && 'border-transparent px-0 hover:border-transparent active:border-transparent'} onPress={() => {
            setEpubExportModalDisplay('input');
            epubExportModalState.open();
          }}>
            <Typography weight='bold' className='text-text-lighter hover:text-text-light transition-colors flex gap-1 items-center justify-center'><IconDownload/> EPub</Typography>
          </Button>
        </div>
      </LoginToUse>

      {epubExportModalState.isOpen && (
        <OverlayContainer>
          <Modal
            isOpen
            title={`${label} EPub Download`}
            onClose={() => epubExportModalState.close()}
            showCloseButton={!loading}
            isDismissable={!loading}
            footerButtons={<>
              <Button
                isDisabled = {isDisabled || loading}
                onPress={
                  epubDownload
                    ? undefined
                    : startEpubDownload
                }
              >
                Download EPub
              </Button>
            </>}
          >
            {tooManyRequests && <Banner size='sm' color='error' children={tooManyRequests}/>}
            <ThingSwitcher thing = {epubExportModalDisplay}>
              <Thing label = 'input'>
                {(forceFilters != true) && (
                  <Checkbox
                    label = 'Include only the lots that match my filters'
                    isSelected = {includeFilters}
                    isDisabled = {loading}
                    onChange = {() => setIncludeFilters((prev) => !prev)}
                  />
                )}
                {loading &&
                  <div className='flex flex-row gap-3 items-center w-32 h-32 max-w-32 max-h-32 justify-center m-auto'>
                    <SpinnerInline/>
                  </div>
                }
              </Thing>
              <Thing label = 'result'>
                {epubGenerationError && <Banner color='error' size='sm' children= {epubGenerationError}/>}
              </Thing>
            </ThingSwitcher>
          </Modal>
        </OverlayContainer>
      )}
      {pdfExportModalState.isOpen && (
        <OverlayContainer>
          <Modal
            isOpen
            title={`${label} PDF Generator`}
            onClose={() => pdfExportModalState.close()}
            showCloseButton
            isDismissable
            footerButtons={<>
              <Button
                isDisabled={isDisabled || loading}
                onPress={startPDFGeneration}
              >
                Generate my PDF
              </Button>
            </>}
          >
            <div className='flex flex-col gap-3'>
              {tooManyRequests && <Banner color='error' size='sm' children={tooManyRequests}/>}
              <ThingSwitcher thing = {pdfExportModalDisplay}>
                <Thing label = 'input'>
                  <div className='flex flex-col gap-2'>
                    <TextField
                      type='text'
                      label='Email Address'
                      value={emailAddress}
                      onChange={(value) => setEmailAddress(value)}
                      autoComplete = 'email'
                      isDisabled = {isDisabled || loading}
                    />
                    <Button
                      variant='link'
                      isDisabled = {isDisabled || loading}
                      onPress={() => {setShowPdfAdvancedOptions(!showPdfAdvancedOptions);}}
                      className='flex gap-2 items-center p-0'
                    >
                      <IconSettings/> {`${showPdfAdvancedOptions ? 'Hide' : 'Show'} Advanced Options`}
                    </Button>
                  </div>
                  <div>
                    {(showPdfAdvancedOptions) && (
                      <div className='flex flex-col gap-2'>
                        <Picker
                          name='size'
                          label='Paper Size'
                          placeholder='Select a paper size...'
                          onSelectionChange={(value) => setPaperSize(value.toString())}
                          selectedKey={paperSize}
                          isDisabled = {loading}
                          className='min-w-64'
                        >
                          <Item key='letter'>Letter</Item>
                          <Item key='legal'>Legal</Item>
                        </Picker>
                        <Picker
                          name='pageOrientation'
                          label='Page Orientation'
                          placeholder='Select a page orientation...'
                          onSelectionChange={(value) => setPageOrientation(value.toString())}
                          selectedKey={pageOrientation}
                          isDisabled = {loading}
                          className='min-w-64'
                        >
                          <Item key='portrait'>Portrait</Item>
                          <Item key='landscape'>Landscape</Item>
                        </Picker>
                        <Picker
                          name='fontSize'
                          label='Font Size'
                          placeholder='Select a font size...'
                          onSelectionChange={(value) => setFontSize(value.toString())}
                          selectedKey={fontSize}
                          isDisabled = {loading}
                          className='min-w-64'
                        >
                          <Item key={8}>Smallest (8pt)</Item>
                          <Item key={10}>Smaller (10pt)</Item>
                          <Item key={12}>Small (12pt)</Item>
                          <Item key={16}>Medium (16pt)</Item>
                          <Item key={18}>Large (18pt)</Item>
                          <Item key={20}>Extra-Large (20pt)</Item>
                        </Picker>
                        <Picker
                          name='marginSize'
                          label='Margin Size'
                          placeholder='Select a margin size...'
                          onSelectionChange={(value) => setMarginSize(value.toString())}
                          selectedKey={marginSize}
                          isDisabled = {loading}
                          className='min-w-64'
                        >
                          <Item key={8}>Extra Small (8px)</Item>
                          <Item key={16}>Small (16px)</Item>
                          <Item key={32}>Medium (32px)</Item>
                          <Item key={72}>Large (72px)</Item>
                        </Picker>
                      </div>
                    )}
                  </div>
                  <div>
                    {(forceFilters != true) && (
                      <Checkbox
                        label = 'Include only the lots that match my filters'
                        isSelected = {includeFilters}
                        isDisabled = {isDisabled || loading}
                        onChange = {() => setIncludeFilters((prev) => !prev)}
                      />
                    )}
                  </div>
                  <div>
                    The email address you enter into this form will exclusively be used to send
                    you your {label} PDF when it is complete. It will not be stored or transfered
                    to third parties.
                  </div>
                  {inputErrors.length > 0 && <Banner color='error' size='sm'>
                    <div className='flex flex-col gap-3'>
                      {inputErrors.map((iError) => (
                        <div key={iError}>{iError}</div>
                      ))}
                    </div>
                  </Banner>}
                  {loading &&
                    <div className='flex flex-row gap-3 items-center w-32 h-32 max-w-32 max-h-32 justify-center m-auto'>
                      <SpinnerInline/>
                    </div>
                  }
                </Thing>
                <Thing label = 'result'>
                  {pdfGenerationError
                    ? <Banner color='error' size='sm' children = {pdfGenerationError}/>
                    : (
                      <div>
                        Your {label} PDF is being created and a link will be emailed to '{emailAddress}'
                      </div>
                    )
                  }
                  <div>
                    <Button
                      onPress={() => {
                        setPdfExportModalDisplay('input');
                        setPaperSize('letter');
                      }}
                    >
                      Generate a New PDF
                    </Button>
                  </div>
                </Thing>
              </ThingSwitcher>
            </div>
          </Modal>
        </OverlayContainer>
      )}
    </div>
  );
};
